import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../../common/Modal";
import { searchTripsFacets, getUserTrips } from "../../../services/trip";
import { formatCurrency } from "../../../utils/currency";
import { adventureStyleLabelMap, adventureStyleIconMap, adventureStyleOptions, weekendOption } from "../../../constants/adventureStyles";
import { ImageResizable } from "../../elements/ImageResizeable";
import { parseAdvancedFilters } from "../../../home/filterQuery";
import { useIsMobile } from "../../../hooks/responsive";
import { getDisplayName } from "../../../utils/userDisplayName";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/* ── Airbnb-style dual-handle range slider ─────────────────────────── */

const THUMB_SIZE = 32;
const TRACK_HEIGHT = 2;

/** Light haptic tap — bridges to native expo-haptics when in-app, falls back to Web Vibration API. */
const triggerHaptic = () => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "haptic" }));
  } else if (navigator.vibrate) {
    navigator.vibrate(10);
  }
};


const DualRangeSlider = ({
  min,
  max,
  valueMin,
  valueMax,
  step = 1,
  onChange,
  onChangeCommitted,
  histogram,
  histogramMaxCount,
  stepLabels,
  className = "",
}) => {
  const trackRef = useRef(null);
  const [dragging, setDragging] = useState(null); // "min" | "max" | null
  const [localMin, setLocalMin] = useState(valueMin);
  const [localMax, setLocalMax] = useState(valueMax);
  const latestLocal = useRef({ min: valueMin, max: valueMax });
  const onChangeCommittedRef = useRef(onChangeCommitted);
  onChangeCommittedRef.current = onChangeCommitted;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // Refs let the drag handler read latest values without re-subscribing
  // window listeners on every pixel of movement.
  const localMinRef = useRef(localMin);
  const localMaxRef = useRef(localMax);
  const prevDragValueRef = useRef(null);

  useEffect(() => {
    localMinRef.current = localMin;
    localMaxRef.current = localMax;
  }, [localMin, localMax]);

  useEffect(() => {
    if (!dragging) {
      setLocalMin(valueMin);
      setLocalMax(valueMax);
      latestLocal.current = { min: valueMin, max: valueMax };
      localMinRef.current = valueMin;
      localMaxRef.current = valueMax;
    }
  }, [valueMin, valueMax, dragging]);

  const range = max - min || 1;
  const pctOf = (v) => ((v - min) / range) * 100;
  const leftPct = pctOf(localMin);
  const rightPct = 100 - pctOf(localMax);

  const valueFromClientX = useCallback(
    (clientX) => {
      const el = trackRef.current;
      if (!el) return min;
      const rect = el.getBoundingClientRect();
      // Account for the padding (half-thumb on each side)
      const trackLeft = rect.left + THUMB_SIZE / 2;
      const trackWidth = rect.width - THUMB_SIZE;
      const ratio = Math.max(0, Math.min(1, (clientX - trackLeft) / trackWidth));
      const raw = min + ratio * range;
      return Math.round(raw / step) * step;
    },
    [min, range, step]
  );

  const handlePointerDown = useCallback(
    (which) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      prevDragValueRef.current = null;
      triggerHaptic();
      setDragging(which);
    },
    []
  );

  // When tapping the track directly (not a thumb), pick the nearest thumb
  // and jump it to that position. Solves the overlap problem where one
  // thumb covers the other and makes it un-grabbable.
  const handleTrackPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const val = valueFromClientX(clientX);
      const distMin = Math.abs(val - localMinRef.current);
      const distMax = Math.abs(val - localMaxRef.current);
      const which = distMin <= distMax ? "min" : "max";
      if (which === "min") {
        const nextMin = Math.min(val, localMaxRef.current);
        setLocalMin(nextMin);
        localMinRef.current = nextMin;
        latestLocal.current = { min: nextMin, max: localMaxRef.current };
        if (onChangeRef.current) onChangeRef.current({ min: nextMin, max: localMaxRef.current });
      } else {
        const nextMax = Math.max(val, localMinRef.current);
        setLocalMax(nextMax);
        localMaxRef.current = nextMax;
        latestLocal.current = { min: localMinRef.current, max: nextMax };
        if (onChangeRef.current) onChangeRef.current({ min: localMinRef.current, max: nextMax });
      }
      setDragging(which);
    },
    [valueFromClientX]
  );

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      // Prevent browser scroll and modal/drawer drag while sliding
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const val = valueFromClientX(clientX);
      // Haptic tick when crossing a step boundary
      if (val !== prevDragValueRef.current) {
        prevDragValueRef.current = val;
        triggerHaptic();
      }
      if (dragging === "min") {
        const nextMin = Math.min(val, localMaxRef.current);
        setLocalMin(nextMin);
        localMinRef.current = nextMin;
        latestLocal.current = { min: nextMin, max: localMaxRef.current };
        if (onChangeRef.current) onChangeRef.current({ min: nextMin, max: localMaxRef.current });
      } else {
        const nextMax = Math.max(val, localMinRef.current);
        setLocalMax(nextMax);
        localMaxRef.current = nextMax;
        latestLocal.current = { min: localMinRef.current, max: nextMax };
        if (onChangeRef.current) onChangeRef.current({ min: localMinRef.current, max: nextMax });
      }
    };
    const up = () => {
      setDragging(null);
      if (onChangeCommittedRef.current) {
        onChangeCommittedRef.current(latestLocal.current);
      }
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", up);
    };
  }, [dragging, valueFromClientX]);

  const showHistogram = histogram && histogram.length > 0 && histogramMaxCount > 0;
  const HIST_HEIGHT = 48;
  const halfThumb = THUMB_SIZE / 2;
  const hasStepLabels = stepLabels && stepLabels.length > 0;
  // Thicker track for segmented/step mode to match Airbnb's bold dash style
  const trackH = hasStepLabels ? 3 : TRACK_HEIGHT;
  // Track vertical centre — flush with histogram bottom, or centred when no histogram
  const trackY = showHistogram ? HIST_HEIGHT : halfThumb;
  const LABEL_AREA = hasStepLabels ? 24 : 0;

  return (
    <div className={`tw-relative tw-select-none ${className}`} style={{ touchAction: "none" }}>
      <div
        ref={trackRef}
        className="tw-relative tw-w-full"
        style={{
          paddingLeft: halfThumb,
          paddingRight: halfThumb,
          height: trackY + halfThumb + LABEL_AREA,
        }}
        onMouseDown={handleTrackPointerDown}
        onTouchStart={handleTrackPointerDown}
      >
        {/* Histogram bars — bottom-aligned flush with the track line */}
        {showHistogram && (
          <div
            className="tw-flex tw-items-end tw-gap-px tw-w-full"
            style={{ height: HIST_HEIGHT }}
          >
            {histogram.map((bucket, i) => {
              const barH = Math.max(2, (Math.sqrt(bucket.count) / Math.sqrt(histogramMaxCount)) * HIST_HEIGHT);
              const bucketMid = (bucket.min + bucket.max) / 2;
              const inRange = bucketMid >= localMin && bucketMid <= localMax;
              return (
                <div
                  key={`${bucket.min}-${i}`}
                  className="tw-flex-1 tw-min-w-0 tw-rounded-t-sm tw-transition-colors tw-duration-150"
                  style={{
                    height: barH,
                    backgroundColor: inRange ? "#00bfff" : "#d1d5db",
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Background track */}
        <div
          className="tw-absolute tw-rounded-full tw-bg-gray-300"
          style={{
            height: trackH,
            top: trackY - trackH / 2,
            left: halfThumb,
            right: halfThumb,
          }}
        />
        {/* Active track segment */}
        <div
          className="tw-absolute tw-rounded-full tw-bg-[#00bfff]"
          style={{
            height: trackH,
            top: trackY - trackH / 2,
            left: `calc(${halfThumb}px + (100% - ${THUMB_SIZE}px) * ${leftPct / 100})`,
            right: `calc(${halfThumb}px + (100% - ${THUMB_SIZE}px) * ${rightPct / 100})`,
          }}
        />

        {/* Gap dividers at interior step positions — cut through the track to create a segmented/dashed look */}
        {hasStepLabels && stepLabels.map((sl, i) => {
          if (i === 0 || i === stepLabels.length - 1) return null;
          const pct = ((sl.value - min) / range) * 100;
          return (
            <div
              key={`gap-${sl.value}`}
              className="tw-absolute"
              style={{
                width: 6,
                height: trackH + 8,
                top: trackY - (trackH + 8) / 2,
                left: `calc(${halfThumb}px + (100% - ${THUMB_SIZE}px) * ${pct / 100} - 3px)`,
                backgroundColor: "white",
                zIndex: 2,
              }}
            />
          );
        })}

        {/* Step labels below the track */}
        {hasStepLabels && stepLabels.map((sl) => {
          const pct = ((sl.value - min) / range) * 100;
          return (
            <div
              key={sl.value}
              className="tw-absolute tw-text-xs tw-text-gray-500"
              style={{
                top: trackY + halfThumb + 4,
                left: `calc(${halfThumb}px + (100% - ${THUMB_SIZE}px) * ${pct / 100})`,
                transform: "translateX(-50%)",
                whiteSpace: "nowrap",
              }}
            >
              {sl.label}
            </div>
          );
        })}

        {/* Min thumb */}
        <div
          onMouseDown={handlePointerDown("min")}
          onTouchStart={handlePointerDown("min")}
          className="tw-absolute tw-rounded-full tw-bg-white tw-cursor-pointer"
          style={{
            width: THUMB_SIZE,
            height: THUMB_SIZE,
            top: trackY - halfThumb,
            left: `calc((100% - ${THUMB_SIZE}px) * ${leftPct / 100})`,
            boxShadow: "0 2px 6px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.04)",
            touchAction: "none",
            zIndex: dragging === "min" ? 5 : 3,
          }}
        />
        {/* Max thumb */}
        <div
          onMouseDown={handlePointerDown("max")}
          onTouchStart={handlePointerDown("max")}
          className="tw-absolute tw-rounded-full tw-bg-white tw-cursor-pointer"
          style={{
            width: THUMB_SIZE,
            height: THUMB_SIZE,
            top: trackY - halfThumb,
            left: `calc((100% - ${THUMB_SIZE}px) * ${(100 - rightPct) / 100})`,
            boxShadow: "0 2px 6px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.04)",
            touchAction: "none",
            zIndex: dragging === "max" ? 5 : 4,
          }}
        />
      </div>
    </div>
  );
};

const buildFiltersFromSearch = (searchParams) => parseAdvancedFilters(searchParams);

export const AllTripsFilterSheet = ({
  open,
  onClose,
  locationSearch,
  baseQueryParams,
  isInApp,
  user,
  onApplyFilters,
  onFacetBoundsChange,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [upcomingTripsCount, setUpcomingTripsCount] = useState(0);
  const [filters, setFilters] = useState(buildFiltersFromSearch(new URLSearchParams(locationSearch)));
  const [facets, setFacets] = useState({
    duration: { min: 2, max: 14 },
    price: { min: 0, max: 3000, histogram: [] },
    resultCount: 0,
    uniqueProjectCount: 0,
    adventureStyles: [],
  });
  const [durationSliderBounds, setDurationSliderBounds] = useState({ min: 2, max: 14 });
  const [draftDuration, setDraftDuration] = useState(null);
  const [draftPrice, setDraftPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const {
    months,
    destination,
    search,
    trip,
    section,
    userId,
    region,
    project,
    subregion,
    week,
    year,
    broad,
  } = baseQueryParams;

  useEffect(() => {
    if (!open) return;
    setFilters(buildFiltersFromSearch(new URLSearchParams(locationSearch)));
  }, [locationSearch, open]);

  useEffect(() => {
    if (!open || !isInApp || !user?.id) return;
    let cancelled = false;
    getUserTrips(user.id)
      .then((res) => {
        if (!cancelled) {
          setUpcomingTripsCount(res.data?.upcomingTrips?.length || 0);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, isInApp, user?.id]);

  // Reset loaded state when base query params change so next open fetches fresh facets
  // instead of incorrectly short-circuiting via the onlyAdventureStylesChanged optimisation.
  useEffect(() => {
    setHasLoadedOnce(false);
    prevNonAdventureKey.current = null;
  }, [months, destination, search, trip, section, userId, region, project, subregion, week, year, broad]);

  // Track the last non-adventure-style filter snapshot so we can detect
  // whether only adventure styles changed (and skip updating facets display).
  const prevNonAdventureKey = useRef(null);

  useEffect(() => {
    if (!open) return;

    const nonAdventureKey = JSON.stringify({
      d0: filters.durationMin, d1: filters.durationMax,
      p0: filters.priceMin,    p1: filters.priceMax,
    });
    const onlyAdventureStylesChanged =
      hasLoadedOnce && prevNonAdventureKey.current === nonAdventureKey;

    const timeout = setTimeout(async () => {
      setIsLoading(true);
      const advancedFilters = {
        duration_min: filters.durationMin ?? undefined,
        duration_max: filters.durationMax ?? undefined,
        price_min: filters.priceMin ?? undefined,
        price_max: filters.priceMax ?? undefined,
        adventure_styles:
          filters.adventureStyles.length > 0 ? filters.adventureStyles.join(",") : undefined,
        broad_categories:
          filters.broadCategories.length > 0 ? filters.broadCategories.join(",") : undefined,
        weekend: filters.weekend ? "1" : undefined,
        section: section ?? undefined,
      };
      try {
        const response = await searchTripsFacets(
          months,
          destination,
          search,
          trip,
          userId,
          region,
          project,
          subregion,
          week,
          year,
          broad,
          advancedFilters
        );

        const payload = response?.data || {};

        if (onlyAdventureStylesChanged) {
          // Adventure style toggle — only update the result count.
          // The facets display (price histogram, adventure style list) stays stable.
          setFacets((prev) => ({
            ...prev,
            resultCount: payload?.facets?.resultCount || 0,
            uniqueProjectCount: payload?.facets?.uniqueProjectCount ?? prev.uniqueProjectCount ?? 0,
          }));
        } else {
          // Duration or price changed — update all facets.
          const newFacets = {
            duration: payload?.facets?.duration || { min: 2, max: 14 },
            price: payload?.facets?.price || { min: 0, max: 0, histogram: [] },
            resultCount: payload?.facets?.resultCount || 0,
            uniqueProjectCount: payload?.facets?.uniqueProjectCount ?? 0,
            adventureStyles: payload?.facets?.adventureStyles || [],
          };
          setFacets(newFacets);
          // On first load, always set duration bounds (even with filters applied from URL).
          // On subsequent updates, only update when no duration filter is active so the
          // slider range stays fixed while the user adjusts the thumbs.
          const hasDurationFilter =
            filters.durationMin != null || filters.durationMax != null;
          if ((!hasLoadedOnce || !hasDurationFilter) && payload?.facets?.duration) {
            const d = payload.facets.duration;
            if (Number.isFinite(d.min) && Number.isFinite(d.max)) {
              setDurationSliderBounds({ min: d.min, max: Math.max(d.min, d.max) });
            }
          }
          const p = payload?.facets?.price;
          if (p && (Number.isFinite(p.min) || Number.isFinite(p.max))) {
            const pMin = Number.isFinite(p.min) ? p.min : 0;
            const pMax = Number.isFinite(p.max) ? Math.max(p.min ?? 0, p.max) : pMin;
            setFilters((prev) => {
              const clampV = (v) => (v == null ? null : Math.max(pMin, Math.min(pMax, v)));
              const newMin = clampV(prev.priceMin);
              const newMax = clampV(prev.priceMax);
              if (prev.priceMin === newMin && prev.priceMax === newMax) return prev;
              const finalMin =
                newMin != null && newMax != null && newMin > newMax ? newMax : newMin;
              const finalMax =
                newMax != null && newMin != null && newMin > newMax ? newMin : newMax;
              return { ...prev, priceMin: finalMin, priceMax: finalMax };
            });
          }
        }
        prevNonAdventureKey.current = nonAdventureKey;
        setHasLoadedOnce(true);
        // Report bounds to parent so filter pills can show smart labels
        if (onFacetBoundsChange && payload?.facets) {
          const d = payload.facets.duration;
          const p = payload.facets.price;
          onFacetBoundsChange({
            durationMin: d?.min ?? null,
            durationMax: d?.max ?? null,
            priceMin: p?.min ?? 0,
            priceMax: p?.max ?? null,
          });
        }
      } catch (error) {
        console.error("Failed to fetch filter facets:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [filters, open, months, destination, search, trip, userId, region, project, subregion, week, year, broad]);

  const toggleAdventureStyle = (style) => {
    setFilters((current) => {
      const exists = current.adventureStyles.includes(style);
      return {
        ...current,
        adventureStyles: exists
          ? current.adventureStyles.filter((currentStyle) => currentStyle !== style)
          : [...current.adventureStyles, style],
      };
    });
  };

  const handleClearAll = () => {
    setFilters({
      durationMin: null,
      durationMax: null,
      priceMin: null,
      priceMax: null,
      adventureStyles: [],
      broadCategories: [],
      weekend: false,
    });
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const durationSliderMin = durationSliderBounds.min ?? 2;
  const durationSliderMax = durationSliderBounds.max ?? Math.max(durationSliderMin, 14);
  const priceSliderMin = Number.isFinite(facets.price?.min) ? facets.price.min : 0;
  const priceSliderMax =
    Number.isFinite(facets.price?.max) && (facets.price?.max ?? 0) >= priceSliderMin
      ? facets.price.max
      : Math.max(priceSliderMin, 1);
  const priceStep = Math.max(1, Math.round((priceSliderMax - priceSliderMin) / 100));

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  let priceValueMin = clamp(
    filters.priceMin ?? priceSliderMin,
    priceSliderMin,
    priceSliderMax
  );
  let priceValueMax = clamp(
    filters.priceMax ?? priceSliderMax,
    priceSliderMin,
    priceSliderMax
  );
  if (priceValueMin > priceValueMax) {
    [priceValueMin, priceValueMax] = [priceValueMax, priceValueMin];
  }

  const priceHistogram = facets.price?.histogram || [];
  const histogramMaxCount =
    priceHistogram.length > 0
      ? Math.max(...priceHistogram.map((bucket) => bucket.count || 0), 1)
      : 1;

  // Build step labels for the duration slider — one label per day
  const durationStepLabels = (() => {
    const r = durationSliderMax - durationSliderMin;
    if (r <= 0) return [];
    const labels = [];
    for (let v = durationSliderMin; v <= durationSliderMax; v++) {
      labels.push({ value: v, label: `${v}D` });
    }
    return labels;
  })();

  // Before facets load, show all adventure style options so the sheet
  // looks complete on first render. Once facets arrive, keep the three
  // branded styles available, then show styles with results or a selection.
  const visibleAdventureStyles = (() => {
    if (!hasLoadedOnce) {
      return [
        ...adventureStyleOptions.map((opt) => ({ style: opt.value, count: 0 })),
        { style: "weekend", count: 0 },
      ];
    }
    // Build a count lookup from facet data
    const facetCountMap = new Map(facets.adventureStyles.map((f) => [f.style, f.count]));
    const result = [];

    // Use adventureStyleOptions as the canonical order, keeping only styles
    // that are branded, have results, or are currently selected.
    adventureStyleOptions
      .map((opt) => ({ style: opt.value, count: facetCountMap.get(opt.value) || 0 }))
      .filter(
        (f) =>
          ["signature", "original", "wild"].includes(f.style) ||
          f.count > 0 ||
          filters.adventureStyles.includes(f.style)
      )
      .forEach((f) => result.push(f));

    // Weekend remains available after the three branded adventure styles.
    const weekendCount = facetCountMap.get("weekend") || 0;
    if (weekendCount > 0 || filters.weekend) {
      result.push({ style: "weekend", count: weekendCount });
    }

    return result;
  })();

  // For broad-category landing pages (e.g. /all-trips?broad=sea) the BE
  // returns displayedCardCount — the number of cards the user actually sees
  // across the multi-row layout (sea fans a single trip into all+weekend+dive).
  // Falls back to uniqueProjectCount (no months → dedupe by project_id) and
  // resultCount (months selected → list view shows raw rows).
  const displayCount =
    facets.displayedCardCount ??
    (months ? facets.resultCount : (facets.uniqueProjectCount ?? facets.resultCount));
  const resultLabel =
    displayCount != null
      ? `Show ${displayCount} results`
      : t("filter_show_results", { defaultValue: "Show results" });

  const footerContent = (
    <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4">
      <button
        type="button"
        onClick={handleClearAll}
        className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-underline"
      >
        Clear all
      </button>
      <button
        type="button"
        onClick={handleApply}
        disabled={isLoading || !hasLoadedOnce}
        className="tw-rounded-xl tw-bg-[#00bfff] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white disabled:tw-opacity-60"
      >
        {isLoading || !hasLoadedOnce ? "Updating..." : resultLabel}
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filters"
      maxWidth="600px"
      maxHeight="85vh"
      zIndex={100100}
      bodyPadding="tw-px-5 tw-py-0"
      footerContent={footerContent}
      expandOnContentDrag={false}
    >
      <div>
        {!isInApp && isMobile && !user?.id && (
          <div className="tw-py-5">
            <button
              type="button"
              onClick={() => {
                onClose();
                const reroute = `${window.location.pathname}${window.location.search}`;
                navigate({
                  pathname: "/login",
                  search: `?reroute=${encodeURIComponent(reroute || "/")}`,
                });
              }}
              className="tw-w-full tw-text-left"
              style={{
                borderRadius: 16,
                boxShadow: "0 1px 6px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
                padding: 16,
              }}
            >
              <div className="tw-flex tw-items-center tw-gap-4">
                <div
                  className="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100"
                  style={{ width: 56, height: 56 }}
                >
                  <PersonOutlinedIcon style={{ fontSize: 32, color: "#6b7280" }} />
                </div>
                <div className="tw-flex tw-flex-1 tw-flex-col">
                  <p className="tw-text-base tw-font-semibold tw-text-gray-900 tw-leading-tight">
                    {t("filter_login_prompt_title", { defaultValue: "Log in" })}
                  </p>
                  <p className="tw-text-sm tw-text-gray-500 tw-mt-0.5">
                    {t("filter_login_prompt_subtitle", {
                      defaultValue: "View your trips, credits, and more",
                    })}
                  </p>
                </div>
                <ChevronRightIcon style={{ fontSize: 22, color: "#9ca3af", flexShrink: 0 }} />
              </div>
            </button>
          </div>
        )}

        {!isInApp && isMobile && user?.id && (
          <div className="tw-py-5">
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/profile");
              }}
              className="tw-w-full tw-text-left"
              style={{
                borderRadius: 16,
                boxShadow: "0 1px 6px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
                padding: 20,
              }}
            >
              <div className="tw-flex tw-items-center tw-gap-5">
                {/* Left: avatar */}
                <div
                  className="tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-bg-gray-100"
                  style={{ width: 88, height: 88 }}
                >
                  {user.image ? (
                    <ImageResizable
                      src={user.image}
                      alt={getDisplayName(user, "Profile")}
                      size="s"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-bg-gray-200 tw-text-gray-500 tw-text-3xl tw-font-semibold">
                      {(getDisplayName(user) || "?")[0]}
                    </div>
                  )}
                </div>

                {/* Right: stats */}
                <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-center tw-gap-3">
                  <div>
                    <p className="tw-text-xl tw-font-bold tw-text-gray-900 tw-leading-tight">
                      {formatCurrency(user.credits || 0)}
                    </p>
                    <p className="tw-text-xs tw-text-gray-500">
                      {t("credits_label", { defaultValue: "Credits" })}
                    </p>
                  </div>
                  <div className="tw-h-px tw-bg-gray-200" />
                  <div>
                    <p className="tw-text-xl tw-font-bold tw-text-gray-900 tw-leading-tight">
                      {upcomingTripsCount}
                    </p>
                    <p className="tw-text-xs tw-text-gray-500">
                      {t("upcoming_trips")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="tw-mt-4 tw-flex tw-items-center tw-justify-end">
                <span className="tw-text-sm tw-font-medium tw-text-gray-700">
                  {t("filter_view_profile", { defaultValue: "View profile" })}
                </span>
                <ChevronRightIcon style={{ fontSize: 18, color: "#374151", marginLeft: 2 }} />
              </div>
            </button>
          </div>
        )}

        {/* Duration */}
        <div className="tw-py-5">
          <div className="tw-flex tw-items-baseline tw-justify-between">
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">Duration</h3>
            <span className="tw-text-sm tw-text-gray-500">
              {durationSliderMin >= durationSliderMax ? (
                <>{durationSliderMin}D</>
              ) : (
                <>
                  {(draftDuration?.min ?? filters.durationMin ?? durationSliderMin)}D
                  {" – "}
                  {(draftDuration?.max ?? filters.durationMax ?? durationSliderMax)}D
                </>
              )}
            </span>
          </div>
          {durationSliderMin < durationSliderMax && (
            <div className="tw-mt-4">
              <DualRangeSlider
                min={durationSliderMin}
                max={durationSliderMax}
                valueMin={filters.durationMin ?? durationSliderMin}
                valueMax={filters.durationMax ?? durationSliderMax}
                step={1}
                stepLabels={durationStepLabels}
                onChange={({ min, max }) => setDraftDuration({ min, max })}
                onChangeCommitted={({ min: nextMin, max: nextMax }) => {
                  setDraftDuration(null);
                  setFilters((current) => ({
                    ...current,
                    durationMin: nextMin,
                    durationMax: nextMax,
                  }));
                }}
              />
            </div>
          )}
        </div>

        {/* Price range */}
        <div className="tw-py-5">
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">Price range</h3>
          <div className="tw-mt-4">
            <DualRangeSlider
              min={priceSliderMin}
              max={priceSliderMax}
              valueMin={priceValueMin}
              valueMax={priceValueMax}
              step={priceStep}
              histogram={priceHistogram}
              histogramMaxCount={histogramMaxCount}
              onChange={({ min, max }) => setDraftPrice({ min, max })}
              onChangeCommitted={({ min: nextMin, max: nextMax }) => {
                setDraftPrice(null);
                setFilters((current) => ({
                  ...current,
                  priceMin: nextMin,
                  priceMax: nextMax,
                }));
              }}
            />
          </div>
          <div className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-text-sm">
            <div>
              <span className="tw-text-gray-500">Minimum</span>
              <p className="tw-font-medium tw-text-gray-900">
                {formatCurrency(draftPrice?.min ?? priceValueMin)}
              </p>
            </div>
            <div className="tw-text-right">
              <span className="tw-text-gray-500">Maximum</span>
              <p className="tw-font-medium tw-text-gray-900">
                {formatCurrency(draftPrice?.max ?? priceValueMax)}
              </p>
            </div>
          </div>
        </div>

        {/* Adventure styles (weekend + regular adventure styles) */}
        {visibleAdventureStyles.length > 0 && (
          <div className="tw-py-5">
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">Adventure styles</h3>
            <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
              {visibleAdventureStyles.map((facetEntry) => {
                if (facetEntry.style === "weekend") {
                  return (
                    <button
                      key="weekend"
                      type="button"
                      onClick={() => setFilters((c) => ({ ...c, weekend: !c.weekend }))}
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-sm tw-transition-colors tw-duration-150 ${
                        filters.weekend
                          ? "tw-bg-gray-50 tw-text-gray-900 tw-font-medium"
                          : "tw-bg-white tw-text-gray-700"
                      }`}
                      style={{
                        border: `2px solid ${filters.weekend ? "#111827" : "#e5e7eb"}`,
                      }}
                    >
                      {weekendOption.Icon && <weekendOption.Icon style={{ fontSize: 18 }} />}
                      {weekendOption.label}
                    </button>
                  );
                }
                const selected = filters.adventureStyles.includes(facetEntry.style);
                const label = adventureStyleLabelMap[facetEntry.style] || facetEntry.style;
                const IconComponent = adventureStyleIconMap[facetEntry.style];
                const styleOption = adventureStyleOptions.find(
                  (option) => option.value === facetEntry.style
                );
                return (
                  <button
                    key={facetEntry.style}
                    type="button"
                    onClick={() => toggleAdventureStyle(facetEntry.style)}
                    className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-sm tw-transition-colors tw-duration-150 ${
                      selected
                        ? "tw-bg-gray-50 tw-text-gray-900 tw-font-medium"
                        : "tw-bg-white tw-text-gray-700"
                    }`}
                    style={{
                      border: `2px solid ${selected ? "#111827" : "#e5e7eb"}`,
                    }}
                  >
                    {styleOption?.logo ? (
                      <img
                        src={styleOption.logo}
                        alt=""
                        className="tw-h-[18px] tw-w-[18px] tw-object-contain"
                      />
                    ) : (
                      IconComponent && <IconComponent style={{ fontSize: 18 }} />
                    )}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
